import React, { useState, useEffect, useRef } from 'react';
import { Grid, Typography, Box, Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate } from 'react-router-dom';
import { getAllUsers } from '../api';

const Cards = ({ stats }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const cardData = [
    {
      title: 'Total Users',
      value: stats?.total_users || '0',
      icon: <PeopleIcon fontSize="large" color="primary" />,
    },
    {
      title: 'Insurance Policies',
      value: stats?.total_insurances || '0',
      icon: <FactCheckIcon fontSize="large" color="success" />,
    },
    {
      title: 'Loans',
      value: stats?.total_loans || '0',
      icon: <HomeWorkIcon fontSize="large" color="secondary" />,
    },
    {
      title: 'Rent Payments',
      value: stats?.total_rents || '0',
      icon: <ReceiptIcon fontSize="large" color="error" />,
    },
    {
      title: 'Bills & Cards',
      value: stats?.total_bills || '0',
      icon: <CreditCardIcon fontSize="large" color="warning" />,
    },
  ];

  const handleCardClick = async (index) => {
    if (index === 0) {
      try {
        const userList = await getAllUsers();
        setUsers(userList);
        setOpen(true);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    } else {
      navigate('/view-all', { state: { tab: index - 1 } });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setUsers([]);
    setSearchTerm('');
  };

  return (
    <>
      {cardData.map((item, index) => (
        <Grid item xs={12} sm={6} md={4} lg={2.2} key={index}>
          <CardWithTilt item={item} onClick={() => handleCardClick(index)} />
        </Grid>
      ))}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="users-modal-title"
        aria-describedby="users-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: '#121212',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            color: '#fff',
          }}
        >
          <Typography id="users-modal-title" variant="h6" component="h2" mb={2}>
            All Users
          </Typography>
          <input
            type="text"
            placeholder="Search by username..."
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            style={{
              marginBottom: '16px',
              padding: '8px',
              width: '100%',
              borderRadius: '6px',
              border: '1px solid #ccc',
              backgroundColor: '#1e1e1e',
              color: 'white',
            }}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#fff' }}>Username</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Email</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users
                  .filter((user) =>
                    user.username.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((user) => (
                    <TableRow key={user._id}>
                      <TableCell sx={{ color: '#fff' }}>{user.username}</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{user.email}</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{user.role}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              mt: 2,
              color: '#fff',
              borderColor: '#fff',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
};

const CardWithTilt = ({ item, onClick }) => {
  const cardRef = useRef(null);
  const [xv, setXv] = useState(0);
  const [yv, setYv] = useState(0);

  useEffect(() => {
    const ct = cardRef.current;
    let animationFrame;
    let easingActive = false;

    const easeOutQuad = (t) => t * (2 - t);

    const animateEasing = (startX, startY, endX, endY, duration) => {
      const startTime = performance.now();
      const animate = (time) => {
        const progress = Math.min((time - startTime) / duration, 1);
        const easedProgress = easeOutQuad(progress);
        const currentX = startX + (endX - startX) * easedProgress;
        const currentY = startY + (endY - startY) * easedProgress;
        setXv(currentX);
        setYv(currentY);
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          easingActive = false;
        }
      };
      easingActive = true;
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      if (easingActive) return;
      const rect = ct.getBoundingClientRect();
      const se = 42;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const rotateX = (y / rect.height - 0.5) * se;
      const rotateY = (x / rect.width - 0.5) * se;
      animateEasing(xv, yv, rotateX, rotateY, 100);
    };

    const handleMouseLeave = () => {
      animateEasing(xv, yv, 0, 0, 200);
    };

    ct.addEventListener('mousemove', handleMouseMove);
    ct.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      ct.removeEventListener('mousemove', handleMouseMove);
      ct.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrame);
    };
  }, [xv, yv]);

  return (
    <Box
      ref={cardRef}
      onClick={onClick}
      sx={{
        '--sz': 'min(40vh, clamp(80px, 18vw, 290px))', // Reduced size
        '--fz': 'calc(0.038 * var(--sz))',
        '--xv': xv,
        '--yv': yv,
        width: 'var(--sz)',
        height: 'calc(0.6 * var(--sz))', // Reduced height
        maxWidth: 'calc(80vw - 15px)',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        transformStyle: 'preserve-3d',
        perspective: '1200px',
        cursor: 'pointer',
        fontFamily: '"Mona Sans", sans-serif',
        borderRadius: '40px'
      }}
    >
      <Box
        className="vortex"
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          '& svg': {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '-6%',
            width: '100%',
            margin: 'auto',
            opacity: 0.1,
          },
        }}
      >
        <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 501 297.81">
          <ellipse cx="251.32" cy="130.01" rx="248.36" ry="125.82" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10, strokeWidth: '2px' }} />
          <ellipse cx="251.73" cy="150.5" rx="160.66" ry="81.39" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10, strokeWidth: '1.6px' }} />
          <ellipse cx="251.43" cy="186.57" rx="107.6" ry="54.51" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m123.45,22.63c52.05,62.7,97.93,131.45,114.34,213.52.41,2.05,1.23,5.33,1.23,5.33,4.92,18.85,7.79,35.66,10.88,54.9" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m248.86,4.6c2.05,97.95,6.15,194.67,9.31,291.95" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m35.75,67.71c6.15,2.05,11.89,5.33,17.62,8.61,21.31,13.11,40.16,24.59,59.84,40.16,33.61,26.23,56.97,49.59,82.38,84.02,2.46,3.28,6.97,10.25,8.61,13.52,18.03,26.23,31.15,52.46,37.22,82.46" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m5.42,127.55c19.35,2.86,38.36,7.77,56.78,14.33,18.57,6.62,36.53,14.92,53.8,24.41,17.83,9.79,34.96,20.69,50.32,34.09,13.95,12.16,26.41,26.36,36.86,41.65,10.26,14.99,18.58,31.3,24.51,48.48.74,2.15,1.45,4.31,2.11,6.49" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m33.29,190.66c47.95-12.7,102.05-4.51,143.85,27.46,27.05,20.49,45.9,47.54,56.53,78.63" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m137.39,241.48c11.89-12.7,29.1-22.54,46.31-19.26,36.07,5.74,50.82,43.44,61.11,73.93" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m458.7,61.16c-46.31,23.36-84.84,58.61-119.26,98.77-10.66,13.11-18.85,25.41-27.05,39.34-17.21,31.15-31.15,63.11-36.91,98.23" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m495.58,115.25c-42.21,8.61-80.74,25.82-117.62,52.05-31.97,22.13-58.2,46.72-75.41,81.15-.82.82-2.05,3.69-2.87,4.51-8.2,14.75-13.52,28.28-18.2,44.71" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m479.19,180.01c-18.21-1.09-36.54-.34-54.6,2.23-18.1,2.57-35.95,6.98-53.15,13.19-17.54,6.34-34.66,14.39-49.94,25.16-12.74,8.98-24.38,19.6-34.01,31.88-9.22,11.75-16.53,25.03-21.02,39.3-.57,1.8-1.09,3.61-1.56,5.44" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m384.93,236.57c-14.34-13.52-34.84-25-55.33-16.8-31.97,13.11-50,45.9-60.17,77.81" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="3.37" cy="127.14" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <path d="m378.37,21.81c-33.2,38.93-57.79,84.02-77.46,131.56-18.85,45.9-31.15,94.26-36.16,143.74" style={{ fill: 'none', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="36.16" cy="67.3" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="33.7" cy="191.07" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="136.16" cy="242.71" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="384.52" cy="236.16" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="479.6" cy="180.42" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="497.63" cy="114.84" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="459.11" cy="60.75" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="377.96" cy="22.22" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="249.27" cy="3.37" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
          <circle cx="123.04" cy="22.22" r="2.87" style={{ fill: '#fff', stroke: '#fff', strokeMiterlimit: 10 }} />
        </svg>
      </Box>
      <Box
        className="mist"
        sx={{
          position: 'absolute',
          width: '140%',
          height: '100%',
          bottom: '-6%',
          left: '-20%',
          boxShadow: 'inset 0 0 calc(var(--sz) / 6) calc(var(--sz) / 10) #e0b256',
          borderRadius: 'calc(var(--sz) / 2)',
        }}
      />
      <Box
        className="orbs"
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          '& .hide-orb': {
            '--ax': 'calc((3 / 3.6) * var(--sz))',
            position: 'absolute',
            left: 0,
            right: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'calc((1 / 3) * var(--sz))',
            boxShadow: 'inset 0 calc((0.78) * var(--sz)) calc((0.08) * var(--sz)) calc((-0.18) * var(--sz)) #1e1e1e',
          },
          '& .orb': {
            position: 'absolute',
            width: 'calc(var(--sz) / 6)',
            height: 'calc(var(--sz) / 6)',
            background: '#e0b256',
            boxShadow: '0 -2px 12px -3px #e0b256, inset 8px -4px 2px 0 #fff2, inset 0 0 12px 0 #fffc, inset 0 6px 12px -6px #0008, inset 0 32px 12px -12px #0003',
            borderRadius: '100%',
            left: 0,
            right: 0,
            margin: 'auto',
            bottom: '20%',
            opacity: 0,
            animation: 'orbX 24s cubic-bezier(0.11, 0, 0.5, 0) infinite, orbY 24s cubic-bezier(0.64, 0, 0.92, 0) infinite',
            animationDelay: 'var(--offset)',
            '&:nth-child(1)': { '--startX': 'calc(-0.6 * var(--sz))', '--startY': 0, '--offset': '0s' },
            '&:nth-child(2)': { '--startX': 'calc(0.6 * var(--sz))', '--offset': '4s' },
            '&:nth-child(3)': { '--startX': 'calc(-0.3 * var(--sz))', '--startY': 'calc(-0.3 * var(--sz))', '--offset': '8s' },
            '&:nth-child(4)': { '--startX': 'calc(0.3 * var(--sz))', '--startY': 'calc(-0.3 * var(--sz))', '--offset': '12s' },
            '&:nth-child(5)': { '--startX': 'calc(-0.6 * var(--sz))', '--startY': 'calc(-0.15 * var(--sz))', '--offset': '16s' },
            '&:nth-child(6)': { '--startX': 'calc(0.6 * var(--sz))', '--startY': 'calc(-0.15 * var(--sz))', '--offset': '20s' },
            '& .dot': {
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              bottom: 0,
              margin: 'auto',
              width: '2px',
              height: '1px',
              background: '#fff',
              transformOrigin: '0 -36px',
              rotate: '0deg',
              boxShadow: '0 0 4px 1px #fff',
              animation: 'innerDot 10s linear infinite, innerDot2 6s ease-in-out infinite',
              '&:nth-child(2)': {
                animation: 'innerDot 12s linear infinite, innerDot2 5s ease-in-out infinite',
                animationDelay: '-2s',
                scale: 0.8,
              },
              '&:nth-child(3)': {
                animation: 'innerDot 8s linear infinite, innerDot2 4s ease-in-out infinite',
                animationDelay: '-6s',
                animationDirection: 'reverse',
                scale: 0.7,
              },
              '&:nth-child(4)': {
                animation: 'innerDot 22s linear infinite, innerDot2 5s ease-in-out infinite',
                animationDelay: '-5s',
              },
              '&:nth-child(5)': {
                animation: 'innerDot 8s linear infinite, innerDot2 3.4s ease-in-out infinite',
                animationDelay: '-1s',
                animationDirection: 'reverse',
                scale: 0.76,
              },
              '&:nth-child(6)': {
                animation: 'innerDot 8.6s linear infinite, innerDot2 3.4s ease-in-out infinite',
                animationDelay: '-2s',
                scale: 0.8,
              },
              '&:nth-child(7)': {
                animation: 'innerDot 9.2s linear infinite, innerDot2 3.4s ease-in-out infinite',
                animationDelay: '-3s',
                animationDirection: 'reverse',
                scale: 0.9,
              },
            },
          },
        }}
      >
        <Box className="hide-orb" />
        <Box className="orb"><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /></Box>
        <Box className="orb"><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /></Box>
        <Box className="orb"><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /></Box>
        <Box className="orb"><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /></Box>
        <Box className="orb"><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /></Box>
        <Box className="orb"><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /><Box className="dot" /></Box>
      </Box>
      <Box
        className="tilt"
        sx={{
          width: '86%',
          height: '86%',
          border: '1px solid #e0b256',
          borderRadius: '18px',
          transition: 'all 0.4s ease-out',
          boxShadow: '0 0 0 10000px #1e1e1e',
          background: `linear-gradient(
            calc(${yv} * -0.4deg),
            transparent,
            transparent calc(20% + (-3px * ${xv})),
            rgba(255, 255, 255, 0.06) calc(40% + (-3px * ${xv})),
            rgba(255, 255, 255, 0.02) calc(70% + (-3px * ${xv})),
            transparent calc(90% + (-3px * ${xv})),
            transparent
          )`,
          position: 'relative',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            width: 'calc(100% + 24px)',
            height: 'calc(100% + 24px)',
            borderRadius: '28px',
          },
          padding: '10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Box>{item.icon}</Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: 'calc(3.2 * var(--fz))',
              fontWeight: 800,
              lineHeight: '116%',
              background: 'linear-gradient(0deg, #fff8 0%, #fffd 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {item.value}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: 'calc(1.2 * var(--fz))',
              fontWeight: 600,
              lineHeight: '142%',
              color: '#fff8',
            }}
          >
            {item.title}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Cards;

const globalStyles = `
  @import url("https://fonts.cdnfonts.com/css/mona-sans");
`;
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = globalStyles;
document.head.appendChild(styleSheet);